import React, { startTransition, useEffect, useState, useMemo, useContext } from 'react';
import SearchPageSidebarSection from './SearchPageSidebarSection';
import SearchPageSidebarTagGroup from './SearchPageSidebarTagGroup';
import { array, bool, func, number, object, oneOfType, string } from 'prop-types';
import PriceRange from './PriceRange';
import constants from '../constants.json';

/**
 * CONFIGURATION
 */

/* Sort types */
const sortMap = {
    SORT_TYPE_DEFAULT: {
        param: '',
        text: 'Default',
    },
    SORT_TYPE_ID_ASC: {
        param: 'id_asc',
        text: 'Older',
    },
    SORT_TYPE_ID_DESC: {
        param: 'id_desc',
        text: 'Newer',
    },
    SORT_TYPE_AZ_ASC: {
        param: 'title_asc',
        text: 'Title: A-Z, ascending',
    },
    SORT_TYPE_AZ_DESC: {
        param: 'title_desc',
        text: 'Title: A-Z, descending',
    },
    SORT_TYPE_PRICE_ASC: {
        param: 'price_asc',
        text: 'Price: lowest to highest',
    },
    SORT_TYPE_PRICE_DESC: {
        param: 'price_desc',
        text: 'Price: highest to lowest',
    },
    SORT_TYPE_TOP_RATED: {
        param: 'top_rated',
        text: 'Product: Top Rated',
    },
    SORT_TYPE_BEST_SELLING: {
        param: 'best_selling',
        text: 'Product: Best Selling',
    },
};

/* Limit Values */
export const median = (arr = []) => {
    const sortedArr = [...arr]
        .filter((n) => !isNaN(parseInt(n)))
        .map((n) => parseInt(n));
    const half = Math.floor(sortedArr.length / 2);
    return sortedArr.length % 2 === 0 ?
        sortedArr[half] : (sortedArr[half - 1] + sortedArr[half]) / 2;
};
export const limitSet = constants.SEARCH_PAGE.RESULT_LIMITS;

/**
 * @function isValidPrice
 * @param {*} price
 * @param {*} comparePrice
 * @param {*} allowZero
 * @returns
 */
const isValidPrice = (price, comparePrice = null, allowZero = false) => {
    if (price === '' || price === undefined || price === null) {
        return null;
    }
    if (isNaN(parseFloat(price))) {
        return false;
    }
    const defaultValue = allowZero ? -1 : 0;
    return parseFloat(price) > (isValidPrice(comparePrice) ? parseFloat(comparePrice) : defaultValue);
};

export default function SearchPageSidebar({ searchQuery, setSearchQuery, vendorList, tagPrefixes, tagList, searchLoading, productCount, productMaxPrice }) {
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [selectedVendor, setSelectedVendor] = useState('');
    const [sortType, setSortType] = useState('');
    const [limit, setLimit] = useState('');
    const handlePriceChange = useContext((minPrice, maxPrice) => {
        const minValid = isValidPrice(minPrice, null, true);
        const maxValid = isValidPrice(maxPrice, minPrice, false);
        if (minValid === false && maxValid === false) {
            return;
        }
        setSearchQuery({
            ...searchQuery,
            minPrice: minValid ? parseFloat(minPrice).toFixed(2) : '',
            maxPrice: maxValid ? parseFloat(maxPrice).toFixed(2) : '',
        });
    }, [searchQuery, setSearchQuery]);
    useEffect(() => {
        startTransition(() => {
            setMinPrice(searchQuery.minPrice ?? '');
            setMaxPrice(searchQuery.maxPrice ?? '');
        });
    }, [searchQuery.minPrice, searchQuery.maxPrice]);
    const searchVendor = useMemo(() => searchQuery?.v ?? '', [searchQuery]);
    useEffect(() => {
        startTransition(() => {
            setSelectedVendor(searchVendor);
        });
    }, [searchVendor]);
    useEffect(() => {
        startTransition(() => {
            setSortType(searchQuery.sortBy);
        });
    }, [searchQuery.sortBy]);
    useEffect(() => {
        startTransition(() => {
            setLimit(limitSet.includes(parseInt(searchQuery.limit)) ? searchQuery.limit : '');
        });
    }, [searchQuery.limit]);
    const handleVendorChange = (newVendorValue) => {
        setSelectedVendor(newVendorValue);
        setSearchQuery({
            ...searchQuery,
            v: newVendorValue ?? '',
        });
    };
    /* Custom sort */
    const handleSetSortType = (e) => {
        e.preventDefault();
        let { value } = e.target;
        const findValueParamInSortMap = Object.values(sortMap)
            .filter((v) => v.param === value);
        if (!findValueParamInSortMap.length) {
            value = sortMap[Object.keys(sortMap)[0]].param;
            return;
        }
        setSortType(value);
        setSearchQuery({
            ...searchQuery,
            sortBy: value,
        });
    };
    /* End custom sort */
    /* Custom limit */
    const handleSetLimit = (e) => {
        e.preventDefault();
        let { value } = e.target;
        value = parseInt(value);
        if (isNaN(value) || value < Math.min(limitSet) || value > Math.max(limitSet)) {
            value = median(limitSet);
        }
        setLimit(value);
        setSearchQuery({
            ...searchQuery,
            limit: parseInt(value),
        });
    };
    return (<div
        className={'search-page--sidebar'}>
        <div
            className={'search-filters-container'}>
            <SearchPageSidebarSection
                headingText={'Price'}>
                <PriceRange
                    minPrice={0}
                    maxPrice={productMaxPrice}
                    queryMinPrice={minPrice}
                    queryMaxPrice={maxPrice}
                    handlePriceChange={handlePriceChange}
                />
            </SearchPageSidebarSection>
            {tagPrefixes.length > 0 && tagPrefixes.map((tagPrefix) => (<SearchPageSidebarSection
                headingText={tagPrefix.tagPrefixValue}
                key={tagPrefix.tagPrefixValue}
                itemCount={tagList.filter((tag) => tag.tagPrefix === tagPrefix.tagPrefixValue).length}>
                <SearchPageSidebarTagGroup
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    tagPrefix={tagPrefix.tagPrefixValue}
                    tagList={tagList.filter((tag) => tag.tagPrefix === tagPrefix.tagPrefixValue)}
                    productCount={productCount} />
            </SearchPageSidebarSection>))}
            <SearchPageSidebarSection
                className={'filter-controls-section'}
            >
                <div
                    className={'filter-controls-wrapper'}>
                    <div
                        className={'search-sidebar--input-group filter-control-item'}
                    >
                        <label
                            htmlFor={'vendors-select'}
                            className={'vendor-label'}
                        >
                            Vendors:
                        </label>
                        <select
                            id={'vendors-select'}
                            className={'search-sidebar--vendor-select'}
                            value={selectedVendor}
                            onChange={(e) => handleVendorChange(e.target.value)}
                        >
                            <option
                                value={''}
                            >
                                All
                            </option>
                            {(vendorList.length > 0 && (searchLoading === true || productCount > 0)) && vendorList
                                .filter((vendor) => {
                                    if (productCount > 0) {
                                        return true;
                                    }
                                    return selectedVendor !== '' && vendor.vendor === selectedVendor;
                                })
                                .map((vendor) => (
                                    <option
                                        key={vendor.vendor}
                                        value={vendor.vendor}
                                    >
                                        {vendor.vendor} ({productCount > 0 ? vendor.vendorCount : 0})
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div
                        className={'sort-filters filter-control-item'}>
                        <div
                            className={'search-sidebar--input-group'}
                        >
                            <label
                                htmlFor={'sort-by-select'}>Sort by:</label>
                            <select
                                id={'sort-by-select'}
                                className={'search-sidebar--input'}
                                name={'Sort by'}
                                value={sortType}
                                onChange={handleSetSortType}
                            >
                                {Object.entries(sortMap).map(([k, v]) => (
                                    <option
                                        key={k}
                                        value={v.param}
                                    >
                                        {v.text}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div
                            className={'search-sidebar--input-group items-per-page'}
                        >
                            <label
                                htmlFor={'page-limit-select'}>Items per page:</label>
                            <select
                                id={'page-limit-select'}
                                className={'search-sidebar--input'}
                                name={'Limit'}
                                value={limit}
                                onChange={handleSetLimit}
                            >
                                <option
                                    value={''}
                                >
                                    Default
                                </option>
                                {limitSet.map((l) => (
                                    <option
                                        key={l}
                                        value={l}
                                    >
                                        {l}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </SearchPageSidebarSection>
        </div>
    </div >);
}

SearchPageSidebar.propTypes = {
    searchQuery: object,
    setSearchQuery: func,
    vendorList: array,
    tagPrefixes: array,
    tagList: array,
    searchLoading: bool,
    productCount: oneOfType([number, string]),
    productMaxPrice: number,
};